// Catalog Shell - Pane Type Registry
// ============================================================================

import { PaneType, PaneInstance, PaneInstanceSpec, PaneInstanceFlags, generateId } from './types';

export class PaneTypeRegistry {
    private types: Map<string, PaneType> = new Map();

    register(type: PaneType): void {
        if (this.types.has(type.typeId)) {
            console.warn(`PaneTypeRegistry: Overwriting existing type '${type.typeId}'`);
        }
        this.types.set(type.typeId, type);
    }

    unregister(typeId: string): boolean {
        return this.types.delete(typeId);
    }

    get(typeId: string): PaneType | null {
        return this.types.get(typeId) ?? null;
    }

    has(typeId: string): boolean {
        return this.types.has(typeId);
    }

    getAll(): PaneType[] {
        return Array.from(this.types.values());
    }

    getAllByKind(kind: 'container' | 'leaf'): PaneType[] {
        return this.getAll().filter(t => t.kind === kind);
    }

    createInstance(spec: PaneInstanceSpec): PaneInstance {
        const type = this.get(spec.typeId);
        if (!type) {
            throw new Error(`Unknown pane type: ${spec.typeId}`);
        }

        const defaultFlags: PaneInstanceFlags = {
            reparentable: type.defaultFlags?.reparentable ?? true,
            cloneable: type.defaultFlags?.cloneable ?? false,
        };

        return {
            instanceId: generateId('pane'),
            typeId: spec.typeId,
            title: spec.title ?? type.defaultTitle,
            context: spec.context ?? {},
            flags: {
                ...defaultFlags,
                ...spec.flags,
            },
            // A restored layout says which panes the user opened (openPane /
            // addColumn set it live); without this a reload made every pane
            // look like part of the initial layout and dropped its ×.
            ...(spec.isDynamic ? { isDynamic: true } : {}),
        };
    }
}
